import { makeContext } from "@shared/utils/makeContext";
import { Program } from "@platform/types/dashboard.types";
import { Organization } from "@platform/types/platform.types";
import { User } from "@shared/features/auth/auth.types";
import { QuizResponse } from "@platform/types/quiz.types";
import { TalkJSData } from "../chat/chat.types";

// --- Name ---
const NAME = "Dashboard";

// --- Types ---
export type DashboardState = {
  programData?: object | null;
  user?: User | null;
  organization?: Organization | null;
  program?: Program | null;
  isLoading?: boolean | null;
  isMobileMenuOpen?: boolean | null;
  quizResponses?: QuizResponse[] | null;
  talkjs?: TalkJSData | null;
};

export type DashboardAction = {
  type: string;
  payload?: Partial<DashboardState>;
};

// --- Constants ---
export const DASHBOARD_SET = "dashboard/set" as const;
export const DASHBOARD_SET_LOADING = "dashboard/set/loading" as const;
export const DASHBOARD_SET_QUIZ_RESPONSES =
  "dashboard/set/quiz-responses" as const;

export const ACTION_TYPES = {
  DASHBOARD_SET,
  DASHBOARD_SET_LOADING,
  DASHBOARD_SET_QUIZ_RESPONSES,
} as const;

// --- Initial State ---
export const initialDashboard: DashboardState = {
  programData: null,
  organization: null,
  program: null,
  isLoading: false,
  quizResponses: null,
  talkjs: null,
};

// --- Reducer ---
function dashboardReducer(
  state: DashboardState,
  action: DashboardAction
): DashboardState {
  switch (action.type) {
    case DASHBOARD_SET:
      return {
        ...state,
        ...action.payload,
        isLoading: false,
      };
    case DASHBOARD_SET_LOADING:
      return {
        ...state,
        isLoading: action.payload?.isLoading ?? false,
      };
    case DASHBOARD_SET_QUIZ_RESPONSES:
      return {
        ...state,
        quizResponses: action.payload?.quizResponses ?? null,
        isLoading: false,
      };
    default:
      throw new Error("Unknown action: " + action.type);
  }
}

// --- Context Creation ---
export const {
  Provider: DashboardProvider,
  useState: useDashboard,
  useDispatch: useDashboardDispatch,
} = makeContext<DashboardState, DashboardAction>({
  name: NAME,
  initialState: initialDashboard,
  reducer: dashboardReducer,
});
